// src/components/forms/shared/QuotePetSelectionRow.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Collapse, IconButton, Typography, styled } from '@mui/material';
import {
  Close as CloseIcon,
  ContentCut as GroomingIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import CustomSelect from '../../common/CustomSelect';
import CustomSelectMenuItem from '../../common/CustomSelectMenuItem';
import { StyledIconButton } from '../../common/Buttons';
import { StyledTextField } from './FormElements';
import {
  PetType,
  PetSize,
  PetBreed,
  referenceDataAPI,
} from '../../../services/api';
import { useTheme } from '@mui/material/styles';

// Sentinel value for the "Show All"/"Show Relevant" row appended as the
// last CustomSelectMenuItem in Breed - test of the inline-toggle approach,
// mirrors the same constant in PetForm.tsx. Intercepted in onChange before
// it reaches handleBreed.
const BREED_SHOW_ALL_TOGGLE_VALUE = '__toggle_show_all_breeds__';

// Sketch-pet draft. Type and Size are required for sketch creation; Breed
// is optional (when null the auto-name falls back to "{Type} #N"). The
// pricing override fields below are optional and populated by the per-row
// stay & grooming modals. localId is a numeric handle the modal hook uses
// to identify this row (the modal hook is typed number | null).
export interface QuoteSketchPetDraft {
  rowId: string;
  localId: number;
  petTypeId: number | '';
  petSizeId: number | '';
  petBreedId: number | '';
  petName: string;
  petNameManuallyEdited?: boolean;

  rateType?: 'Single' | 'Sharing' | 'Custom';
  customRate?: number;
  useSharingRate?: boolean;
  numberOfNights?: number;
  groomSmooth?: number;
  groomLong?: number;
  groomCut?: number;
  lineItemDiscountPercentage?: number;
  lineItemDiscount?: number;
}

interface QuotePetSelectionRowProps {
  draft: QuoteSketchPetDraft;
  petTypes: PetType[];
  // Breeds for this row's current type+size, resolved by the parent from a
  // shared (typeId,sizeId)-keyed cache. The parent owns this fetch so the
  // same cache can also resolve breed names for the quote summary preview -
  // this component no longer fetches breeds itself.
  breeds: PetBreed[];
  // "Show All" toggle state for this row's Breed field - owned by the
  // parent (QuoteForm) alongside its type-only breed cache, the same way
  // `breeds` itself is resolved from a shared cache. Rendered as the last
  // option in the Breed CustomSelect below.
  showAllBreeds?: boolean;
  onToggleShowAllBreeds?: () => void;
  index: number;
  showRemove: boolean;
  bookingLength?: number;
  onChange: (next: QuoteSketchPetDraft) => void;
  onRemove: () => void;
  onEditClick?: (localId: number) => void;
  onGroomingClick?: (localId: number) => void;
}

const RowOuter = styled(Box)(({ theme }) => ({
  padding: '0.6rem 0',
  borderBottom: `3px dashed ${theme.palette.dividerContrast}`,
  '&:first-of-type': { paddingTop: 0 },
  '&:last-of-type': { borderBottom: 'none' },
}));

const RowHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  marginBottom: '0.4rem',
});

const RowIndex = styled(Box)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.secondary,
  fontSize: '1.8rem',
  width: '2.5rem',
  flexShrink: 0,
}));

const FieldStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
});

const ActionStrip = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '1rem',
  alignItems: 'center',
  justifyContent: 'flex-start',
  marginTop: '0.75rem',
  marginBottom: '0.75rem',
  paddingRight: '2.5rem',
  [theme.breakpoints.down('sm')]: {
    paddingRight: '0',
  },
}));

const Badge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '999px',
  minWidth: '1.4rem',
  height: '1.4rem',
  padding: '0 0.35rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.75rem',
  fontWeight: 700,
  pointerEvents: 'none',
  lineHeight: 1,
}));

const formatZAR = (amount: number): string =>
  new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const QuotePetSelectionRow: React.FC<QuotePetSelectionRowProps> = ({
  draft,
  petTypes,
  breeds,
  showAllBreeds = false,
  onToggleShowAllBreeds,
  index,
  showRemove,
  bookingLength,
  onChange,
  onRemove,
  onEditClick,
  onGroomingClick,
}) => {
  const [sizes, setSizes] = useState<PetSize[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!draft.petTypeId) {
      setSizes([]);
      return;
    }
    referenceDataAPI
      .getPetSizesByType(Number(draft.petTypeId))
      .then((data) => {
        if (!cancelled) {
          const SIZE_ORDER = [
            'Small',
            'Small-Medium',
            'Medium',
            'Medium-Large',
            'Large',
            'Giant',
          ];
          setSizes(
            [...data].sort((a, b) => {
              const ai = SIZE_ORDER.findIndex(
                (s) => s.toLowerCase() === a.size.toLowerCase()
              );
              const bi = SIZE_ORDER.findIndex(
                (s) => s.toLowerCase() === b.size.toLowerCase()
              );
              return (
                (ai === -1 ? SIZE_ORDER.length : ai) -
                (bi === -1 ? SIZE_ORDER.length : bi)
              );
            })
          );
        }
      })
      .catch(() => {
        if (!cancelled) setSizes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [draft.petTypeId]);

  const typeLabel = useMemo(
    () =>
      draft.petTypeId
        ? (petTypes.find((t) => t.id === Number(draft.petTypeId))?.type ?? '')
        : '',
    [draft.petTypeId, petTypes]
  );

  const handleType = (val: string) => {
    onChange({
      ...draft,
      petTypeId: val === '' ? '' : Number(val),
      petSizeId: '',
      petBreedId: '',
      petName: draft.petNameManuallyEdited ? draft.petName : '',
    });
  };

  // Auto-name once size is picked: "{Type} #N". Replaced by "{Breed} #N"
  // if the user later picks a breed. Honoured only when the user has not
  // manually edited the name.
  const handleSize = (val: string) => {
    const sizeId = val === '' ? '' : Number(val);
    const autoName =
      sizeId !== '' && typeLabel ? `${typeLabel} #${index + 1}` : '';
    onChange({
      ...draft,
      petSizeId: sizeId,
      petBreedId: '',
      petName: draft.petNameManuallyEdited ? draft.petName : autoName,
    });
  };

  // Picking a breed swaps the auto-name to "{Breed} #N"; clearing the
  // breed reverts to the type-based name.
  const handleBreed = (val: string) => {
    const breedId = val === '' ? '' : Number(val);
    const breedLabel = breeds.find((b) => b.id === Number(val))?.breed ?? '';
    const autoName = breedLabel
      ? `${breedLabel} #${index + 1}`
      : typeLabel
        ? `${typeLabel} #${index + 1}`
        : '';
    onChange({
      ...draft,
      petBreedId: breedId,
      petName: draft.petNameManuallyEdited ? draft.petName : autoName,
    });
  };

  const handlePetName = (val: string) => {
    onChange({ ...draft, petName: val, petNameManuallyEdited: true });
  };

  const sizeVisible = !!draft.petTypeId;
  const breedVisible = sizeVisible && !!draft.petSizeId;
  // Name field and action strip appear once size is picked. Breed is now
  // optional, so we no longer gate these on a breed selection.
  const stripVisible = breedVisible;
  const theme = useTheme();
  const groomingCount = useMemo(
    () =>
      (draft.groomSmooth || 0) + (draft.groomLong || 0) + (draft.groomCut || 0),
    [draft.groomSmooth, draft.groomLong, draft.groomCut]
  );

  const stayLengthDiff =
    draft.numberOfNights !== undefined &&
    bookingLength !== undefined &&
    draft.numberOfNights !== bookingLength
      ? draft.numberOfNights
      : null;

  const discountPct = draft.lineItemDiscountPercentage || 0;

  const summaryParts = useMemo(() => {
    const parts: string[] = [];
    if (draft.rateType === 'Sharing') {
      parts.push('Sharing rate');
    } else if (draft.rateType === 'Custom' && draft.customRate) {
      parts.push(`Custom rate ${formatZAR(draft.customRate)}`);
    }
    if (stayLengthDiff !== null) {
      parts.push(`${stayLengthDiff}/${bookingLength} nights`);
    }
    if (discountPct > 0) {
      parts.push(`${discountPct}% off`);
    }
    return parts;
  }, [
    draft.rateType,
    draft.customRate,
    stayLengthDiff,
    bookingLength,
    discountPct,
  ]);

  const stayCustomised =
    (draft.rateType && draft.rateType !== 'Single') ||
    stayLengthDiff !== null ||
    discountPct > 0;

  return (
    <RowOuter>
      <RowHeader>
        <RowIndex>{index + 1}.</RowIndex>
        {showRemove && (
          <IconButton
            size="medium"
            aria-label={`Remove pet row ${index + 1}`}
            onClick={onRemove}
            sx={{
              marginLeft: 'auto',
              backgroundColor: theme.palette.grey[500],
              padding: '3px',
              mr: '.5rem',
              '& svg': { fontSize: '2.25rem', color: theme.palette.grey[0] },
              '&:hover': { backgroundColor: theme.palette.secondary.main },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </RowHeader>

      <FieldStack>
        <CustomSelect
          placeholder="Type"
          ariaLabel="Type"
          value={draft.petTypeId === '' ? '' : String(draft.petTypeId)}
          onChange={handleType}
        >
          {petTypes.map((t) => (
            <CustomSelectMenuItem key={t.id} value={String(t.id)}>
              {t.type}
            </CustomSelectMenuItem>
          ))}
        </CustomSelect>

        {sizeVisible && (
          <CustomSelect
            placeholder="Size"
            ariaLabel="Size"
            value={draft.petSizeId === '' ? '' : String(draft.petSizeId)}
            onChange={handleSize}
          >
            {sizes.map((s) => (
              <CustomSelectMenuItem key={s.id} value={String(s.id)}>
                {s.size}
              </CustomSelectMenuItem>
            ))}
          </CustomSelect>
        )}

        {breedVisible && (
          <CustomSelect
            placeholder="Breed (optional)"
            ariaLabel="Breed"
            value={draft.petBreedId === '' ? '' : String(draft.petBreedId)}
            onChange={(val) => {
              if (val === BREED_SHOW_ALL_TOGGLE_VALUE) {
                onToggleShowAllBreeds?.();
                return;
              }
              handleBreed(val);
            }}
          >
            {breeds.map((b) => (
              <CustomSelectMenuItem key={b.id} value={String(b.id)}>
                {b.breed}
              </CustomSelectMenuItem>
            ))}
            {onToggleShowAllBreeds && (
              <CustomSelectMenuItem value={BREED_SHOW_ALL_TOGGLE_VALUE}>
                {showAllBreeds ? 'Show Relevant Breeds' : 'Show All Breeds'}
              </CustomSelectMenuItem>
            )}
          </CustomSelect>
        )}

        {stripVisible && (
          <StyledTextField
            placeholder="Pet name"
            value={draft.petName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handlePetName(e.target.value)
            }
            inputProps={{ maxLength: 100 }}
            fullWidth
          />
        )}
      </FieldStack>

      <Collapse in={stripVisible} timeout={200} unmountOnExit>
        <Box>
          <ActionStrip>
            <Box
              sx={{
                position: 'relative',
                width: '3.5rem',
                height: '3.5rem',
              }}
            >
              <StyledIconButton
                variant="bold"
                size="small"
                backgroundColor={theme.palette.primary.main}
                onClick={() => onGroomingClick?.(draft.localId)}
                sx={{
                  width: '3.5rem',
                  height: '3.5rem',
                  opacity: groomingCount > 0 ? 0.5 : 1,
                  transition: 'opacity 0.2s ease',
                  '& svg': {
                    fontSize: '2.25rem',
                    color: theme.palette.grey[0],
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    outline: `3px solid ${theme.palette.primary.dark}`,
                    '& svg': { fontSize: '2.5rem' },
                  },
                }}
                aria-label="Edit grooming"
              >
                <GroomingIcon />
              </StyledIconButton>
              {groomingCount > 0 && (
                <Badge
                  sx={{
                    top: '-6px',
                    right: '-6px',
                    backgroundColor: 'secondary.main',
                    color: 'secondary.contrastText',
                  }}
                >
                  {groomingCount}
                </Badge>
              )}
            </Box>

            <Box
              sx={{
                position: 'relative',
                width: '3.5rem',
                height: '3.5rem',
              }}
            >
              <StyledIconButton
                variant="bold"
                size="small"
                backgroundColor={theme.palette.primary.main}
                onClick={() => onEditClick?.(draft.localId)}
                sx={{
                  width: '3.5rem',
                  height: '3.5rem',
                  opacity: stayCustomised ? 0.5 : 1,
                  transition: 'opacity 0.2s ease',
                  '& svg': {
                    fontSize: '2.25rem',
                    color: theme.palette.grey[0],
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    outline: `3px solid ${theme.palette.primary.dark}`,
                    '& svg': { fontSize: '2.5rem' },
                  },
                }}
                aria-label="Edit stay, rate and discount"
              >
                <EditIcon />
              </StyledIconButton>
              {stayLengthDiff !== null && (
                <Badge
                  sx={{
                    top: '-6px',
                    right: '-6px',
                    backgroundColor: 'secondary.main',
                    color: 'secondary.contrastText',
                  }}
                >
                  {stayLengthDiff}/{bookingLength}
                </Badge>
              )}
              {discountPct > 0 && (
                <Badge
                  sx={{
                    bottom: '-6px',
                    right: '-6px',
                    backgroundColor: 'warning.main',
                    color: 'warning.contrastText',
                  }}
                >
                  {discountPct}%
                </Badge>
              )}
            </Box>
          </ActionStrip>
          {summaryParts.length > 0 && (
            <Typography
              sx={{
                fontSize: '1.9rem',
                color: 'text.secondary',
                mt: '-0.25rem',
                mb: '0.5rem',
              }}
            >
              {summaryParts.join(' · ')}
            </Typography>
          )}
        </Box>
      </Collapse>
    </RowOuter>
  );
};

export default QuotePetSelectionRow;
